/* Galaxy Lighting — combined-shipping quote opt-in.
 *
 * Shopify always preselects the CHEAPEST delivery option, and the
 * "Quote for combined shipping" rate is $0 — so it became the default on
 * almost every order and real freight was never charged. Shopify forbids
 * reordering rates to make a dearer one the default, but hiding is allowed.
 *
 * So: a delivery-customization function hides the quote rate unless the
 * cart attribute `combined_shipping_quote` is set. This script renders the
 * checkbox that sets it, on both the cart page and the cart drawer.
 *
 * 2026-09-14 — the option only makes sense when there is more than one item
 * to combine, so it is now LOCKED below MIN_ITEMS: the checkbox is disabled,
 * the box says why, and if the cart drops back to a single item a previously
 * set attribute is cleared, so a stale opt-in cannot survive. The Shipping
 * Policy link sits in the same box, because the cart is where the customer is
 * actually deciding about freight.
 */
(function () {
  'use strict';

  var KEY = 'combined_shipping_quote';
  var MARK = 'data-galaxy-quote-optin';
  var MIN_ITEMS = 2;
  var POLICY_URL = '/pages/shipping-policy';

  var TITLE = 'Ask us to quote combined shipping';
  var HINT =
    'For orders of ' + MIN_ITEMS + ' items or more. Tick this and we work out one freight ' +
    'charge for the whole order instead of charging per item. You pay for the lights now, ' +
    'and we email you the shipping cost to approve before we dispatch.';
  var LOCKED =
    'Add a second item to use this — a single item already ships at the standard rate.';
  var POLICY_TEXT = 'Read our Shipping Policy';

  var CSS =
    '.galaxy-quote-optin{margin:1.2rem 0;padding:1.2rem 1.4rem;border:1px solid rgba(0,0,0,.12);border-radius:.6rem;}' +
    '.galaxy-quote-optin__row{display:flex;gap:.9rem;align-items:flex-start;cursor:pointer;}' +
    '.galaxy-quote-optin__row input{margin:.25rem 0 0;flex:0 0 auto;width:1.15rem;height:1.15rem;cursor:pointer;}' +
    '.galaxy-quote-optin__title{font-weight:600;line-height:1.4;}' +
    '.galaxy-quote-optin__hint{margin:.5rem 0 0 2.05rem;font-size:1.3rem;line-height:1.5;opacity:.75;}' +
    '.galaxy-quote-optin__note{margin:.5rem 0 0 2.05rem;font-size:1.3rem;line-height:1.5;font-weight:600;}' +
    '.galaxy-quote-optin__policy{display:inline-block;margin:.9rem 0 0 2.05rem;font-size:1.3rem;text-decoration:underline;}' +
    '.galaxy-quote-optin.is-locked .galaxy-quote-optin__row,' +
    '.galaxy-quote-optin.is-locked .galaxy-quote-optin__hint{opacity:.45;}' +
    '.galaxy-quote-optin.is-locked .galaxy-quote-optin__row,' +
    '.galaxy-quote-optin.is-locked .galaxy-quote-optin__row input{cursor:not-allowed;}' +
    '.galaxy-quote-optin.is-busy{opacity:.55;pointer-events:none;}';

  function styles() {
    if (document.getElementById('galaxy-quote-optin-css')) return;
    var s = document.createElement('style');
    s.id = 'galaxy-quote-optin-css';
    s.textContent = CSS;
    document.head.appendChild(s);
  }

  function truthy(v) {
    v = String(v == null ? '' : v).trim().toLowerCase();
    return v === 'yes' || v === 'true' || v === '1' || v === 'on';
  }

  /* -> { on: Boolean, count: Number } */
  function read() {
    return fetch('/cart.js', { headers: { Accept: 'application/json' } })
      .then(function (r) { return r.json(); })
      .then(function (c) {
        var a = (c && c.attributes) || {};
        return { on: truthy(a[KEY]), count: (c && c.item_count) || 0 };
      })
      .catch(function () { return { on: false, count: 0 }; });
  }

  function save(on) {
    var body = { attributes: {} };
    body.attributes[KEY] = on ? 'yes' : '';
    return fetch('/cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body)
    }).catch(function () {});
  }

  var cached = null;
  var inFlight = false;
  var clearing = false;

  function build() {
    var box = document.createElement('div');
    box.className = 'galaxy-quote-optin';
    box.setAttribute(MARK, '');

    var label = document.createElement('label');
    label.className = 'galaxy-quote-optin__row';

    var input = document.createElement('input');
    input.type = 'checkbox';

    var title = document.createElement('span');
    title.className = 'galaxy-quote-optin__title';
    title.textContent = TITLE;

    label.appendChild(input);
    label.appendChild(title);

    var hint = document.createElement('p');
    hint.className = 'galaxy-quote-optin__hint';
    hint.textContent = HINT;

    var note = document.createElement('p');
    note.className = 'galaxy-quote-optin__note';
    note.textContent = LOCKED;
    note.hidden = true;

    var policy = document.createElement('a');
    policy.className = 'galaxy-quote-optin__policy';
    policy.href = POLICY_URL;
    policy.textContent = POLICY_TEXT;

    box.appendChild(label);
    box.appendChild(hint);
    box.appendChild(note);
    box.appendChild(policy);

    input.addEventListener('change', function () {
      if (input.disabled) return;
      cached = { on: input.checked, count: cached ? cached.count : MIN_ITEMS };
      box.classList.add('is-busy');
      save(input.checked).then(function () {
        box.classList.remove('is-busy');
        refresh(true);
      });
    });

    return box;
  }

  /* Write only when the value actually differs — every needless DOM write
     wakes the MutationObserver below and would spin us in a refresh loop. */
  function paint(box, st) {
    var input = box.querySelector('input');
    var note = box.querySelector('.galaxy-quote-optin__note');
    var locked = st.count < MIN_ITEMS;

    if (input.checked !== st.on) input.checked = st.on;
    if (input.disabled !== locked) input.disabled = locked;
    if (box.classList.contains('is-locked') !== locked) {
      if (locked) box.classList.add('is-locked');
      else box.classList.remove('is-locked');
    }
    if (note.hidden === locked) note.hidden = !locked;
  }

  /* Insert above whichever checkout button is on screen. */
  function anchors() {
    var found = [];
    var buttons = document.querySelectorAll('.cart__ctas, [name="checkout"]');
    for (var i = 0; i < buttons.length; i++) {
      var el = buttons[i];
      var host = el.closest('.cart__ctas') || el.parentElement;
      if (host && found.indexOf(host) === -1) found.push(host);
    }
    return found;
  }

  function render(st) {
    var hosts = anchors();
    if (!hosts.length) return;
    styles();

    hosts.forEach(function (host) {
      if (!host.parentElement) return;
      var box = host.parentElement.querySelector('[' + MARK + ']');
      if (!box) {
        box = build();
        host.parentElement.insertBefore(box, host);
      }
      paint(box, st);
    });
  }

  function refresh(force) {
    if (!anchors().length) return;
    if (inFlight) return;
    if (!force && cached) { render(cached); return; }

    inFlight = true;
    read().then(function (st) {
      inFlight = false;

      /* An opt-in must not survive the cart falling back to a single item.
         Paint it off immediately — do NOT wait on the clearing request, or a
         refresh that lands mid-flight will read the stale 'yes' and re-tick
         a box the customer can no longer untick. */
      if (st.count < MIN_ITEMS && st.on) {
        st.on = false;
        if (!clearing) {
          clearing = true;
          save(false).then(function () { clearing = false; });
        }
      }

      cached = st;
      render(st);
    });
  }

  var timer = null;
  function bump() {
    clearTimeout(timer);
    timer = setTimeout(function () { refresh(true); }, 250);
  }

  function boot() {
    refresh(true);
    /* Dawn re-renders cart sections after every quantity change. */
    new MutationObserver(bump).observe(document.body, { childList: true, subtree: true });
    document.addEventListener('cart:refresh', bump);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
